import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Send, Loader2, AlertCircle, ChevronLeft, History, Trash2, Lock, Copy, Check, GitBranch, ClipboardList, Download, Paperclip, X, FileText, Image, Menu, Volume2, VolumeX, Mic, MicOff, Palette, Pause, Play, LayoutGrid, Pencil, Sparkles, ChevronDown, Globe } from 'lucide-react';
import { COMFORT_LANGUAGES, getComfortLanguage, setComfortLanguage, detectComfortLanguageFromText, INDIAN_STATES, getComfortState, setComfortState } from '../utils/comfortLanguage';
import { MapPin } from 'lucide-react';
import { speak as ttsSpeak, stop as ttsStop, pause as ttsPause, resume as ttsResume, isSpeechSupported, getSelectedTextWithin } from '../utils/tts';
import { hasBrowserSTT, hasMediaRecorder, isVoiceInputSupported, startBrowserSTT, startWhisperRecording } from '../utils/voiceInput';
import { techniqueApi, chatApi, uploadApi, lmsApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
const MindMapModal = lazy(() => import('../components/MindMapModal'));
const InfographicMindMap = lazy(() => import('../components/InfographicMindMap'));

// Renders a chat attachment image with graceful fallback when the original file
// has been archived (deleted from disk after the 60-day cutoff).
function AttachmentImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-xs">
        <FileText className="w-4 h-4 opacity-70" />
        <span>📦 Archived — original attachment is no longer available</span>
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-white/20"
      />
    </a>
  );
}

// Techniques the student can re-ask the same question with. Keys match the
// DB technique slugs where applicable so the current technique can be hidden.
const RETRY_TECHNIQUES = [
  { key: 'link-association-for-long-answers', label: 'Link & Association',    prompt: 'Teach the following using the LINK & ASSOCIATION technique — connect each fact to the next with a vivid, chained image. Keep the same concept scene throughout.' },
  { key: 'me-to-me-technique',                label: 'Me to Me',              prompt: 'Teach the following using the ME-TO-ME technique — the student stands in front of a mirror, each body part / layout position becomes a hook for one fact.' },
  { key: 'memory-palace',                     label: 'Memory Palace',         prompt: 'Teach the following using the MEMORY PALACE technique — place each fact at a specific location in the student\'s palace rooms (hooks section only). Keep the conceptual story in a non-palace setting.' },
  { key: 'peg-system',                        label: 'Peg System',            prompt: 'Teach the following using the PEG SYSTEM — attach each fact to a numbered peg image. Follow the 3-digit peg rule (e.g. 938 → 09 + 38).' },
  { key: 'finishing-pages',                   label: 'Finishing Pages',       prompt: 'Teach the following using the FINISHING PAGES - KEYWORDS FILTERING technique — pull out just the critical keywords, group them, then build minimal hooks on top.' },
  { key: 'mental-summary-charts',             label: 'Mental Summary Chart',  prompt: 'Teach the following using the MENTAL SUMMARY CHART (MSC) technique — give substitutions, keywords, and a short pictogram-ready story suitable for a visual poster.' },
  { key: 'story',                             label: 'Story Method',          prompt: 'Teach the following using the STORY METHOD — weave all facts into one short, memorable story. Keep the same concept scene throughout.' },
  { key: 'substitution',                      label: 'Substitution Method',   prompt: 'Teach the following using the SUBSTITUTION method — convert every difficult word / name / formula into a familiar vivid image, then link them.' },
  { key: 'number-shape-rhyme',                label: 'Number Shape / Rhyme',  prompt: 'Teach the following using the NUMBER SHAPE / NUMBER RHYME technique — attach each fact to the shape or rhyme of its number.' },
];

function ComfortLanguageChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const current = COMFORT_LANGUAGES.find(l => l.code === value) || COMFORT_LANGUAGES[0];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50 transition-colors"
        title="Pick the language mix the AI should reply in"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="font-medium">{current.short}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 bottom-full mb-1 z-30 w-64 max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 py-1">
          {['base', 'mix', 'native'].map((grp) => {
            const items = COMFORT_LANGUAGES.filter(l => l.group === grp);
            if (!items.length) return null;
            const header = grp === 'base' ? 'Default' : grp === 'mix' ? 'Mix with English (Latin script)' : 'Full native (native script)';
            return (
              <div key={grp} className={grp !== 'base' ? 'border-t border-gray-100 dark:border-gray-700 mt-0.5 pt-0.5' : ''}>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {header}
                </div>
                {items.map(l => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => { setOpen(false); onChange(l.code); }}
                    className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors ${
                      l.code === value ? 'text-indigo-700 dark:text-indigo-300 font-semibold bg-indigo-50/50 dark:bg-indigo-900/20' : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <span>{l.label}</span>
                    {l.code === value && <Check className="w-3 h-3" />}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StateChip({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const current = INDIAN_STATES.find(s => s.code === value) || INDIAN_STATES[0];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50 transition-colors"
        title="Pick your state so examples feel local"
      >
        <MapPin className="w-3.5 h-3.5" />
        <span className="font-medium truncate max-w-[110px]">{current.label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 bottom-full mb-1 z-30 w-56 max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 py-1">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
            Localize examples to
          </div>
          {INDIAN_STATES.map(s => (
            <button
              key={s.code}
              type="button"
              onClick={() => { setOpen(false); onChange(s.code); }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors ${
                s.code === value ? 'text-emerald-700 dark:text-emerald-300 font-semibold bg-emerald-50/50 dark:bg-emerald-900/20' : 'text-gray-700 dark:text-gray-200'
              }`}
            >
              <span>{s.label}</span>
              {s.code === value && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TechniqueChips({ onPick, disabled, activeIdx, idx, excludeKey }) {
  const isActive = activeIdx === idx;
  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3 h-3 text-purple-500" />
        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Try another technique
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">· 1 question each</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RETRY_TECHNIQUES.filter(t => t.key !== excludeKey).map(t => (
          <button
            key={t.key}
            onClick={() => onPick(t)}
            disabled={disabled}
            className="px-2.5 py-1 text-xs rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 dark:border-purple-900 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isActive && disabled ? (
              <span className="flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" />{t.label}</span>
            ) : t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TechniqueChat() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const [technique, setTechnique] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTechnique, setIsLoadingTechnique] = useState(true);
  const [conversationId, setConversationId] = useState(null);
  const [quota, setQuota] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [mindMapIdx, setMindMapIdx] = useState(null);
  const [infographicIdx, setInfographicIdx] = useState(null);
  const [retryLoading, setRetryLoading] = useState(null);
  const [comfortLang, setComfortLangState] = useState(() => getComfortLanguage());
  const changeComfortLang = (code) => { setComfortLangState(code); setComfortLanguage(code); };
  const [comfortStateCode, setComfortStateCode] = useState(() => getComfortState());
  const changeComfortState = (code) => { setComfortStateCode(code); setComfortState(code); };
  // Tracks which message indices the user has already paid the Visual Mind Map fee for in this session.
  const chargedVisualRef = useRef(new Set());
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [reviewListContent, setReviewListContent] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [speakingPaused, setSpeakingPaused] = useState(false);
  const [voiceMode, setVoiceMode] = useState(null);
  const voiceSessionRef = useRef(null);
  const [imageMindMapLoading, setImageMindMapLoading] = useState(null);
  const [imageMindMapData, setImageMindMapData] = useState(null);
  const imageMindMapCache = useRef({});
  // Mental Summary Chart image (only shown for the mental-summary-charts technique)
  const [mscImageLoading, setMscImageLoading] = useState(null);
  const [mscImageData, setMscImageData] = useState(null);
  const [mscDownloading, setMscDownloading] = useState(false);
  const mscImageCache = useRef({});
  const mscCaptureRef = useRef(null); // DOM node captured for the composite PNG
  // Videos attached to this technique (populated once per technique switch)
  const [techniqueLessons, setTechniqueLessons] = useState([]);
  const reviewCache = useRef({});
  const reviewContentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Reset per-technique state so messages/history from the previous technique
    // don't leak into the new one while its data is still loading.
    setMessages([]);
    setConversationId(null);
    setConversations([]);
    setTechnique(null);
    setSelectedFiles([]);
    setInfographicIdx(null);
    setMindMapIdx(null);
    setImageMindMapData(null);
    setReviewListContent(null);
    setMscImageData(null);
    setTechniqueLessons([]);
    chargedVisualRef.current = new Set();
    imageMindMapCache.current = {};
    mscImageCache.current = {};
    reviewCache.current = {};

    loadTechnique();
    loadQuota();
    loadConversations();
    loadTechniqueLessons();
  }, [slug]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // When another page (e.g. the general Chat) sends the student here with a
  // question in navigation state, drop it into the input so they just hit Send.
  useEffect(() => {
    const prefilled = location.state?.prefilledQuestion;
    if (prefilled) {
      setInput(prefilled);
      // Clear it from history so a back-forward doesn't re-trigger.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.prefilledQuestion, location.pathname, navigate]);

  const loadTechniqueLessons = async () => {
    try {
      const r = await lmsApi.getTechniqueLessons(slug);
      setTechniqueLessons(r.data?.lessons || []);
    } catch (_) { setTechniqueLessons([]); }
  };

  const loadTechnique = async () => {
    try {
      setIsLoadingTechnique(true);
      const response = await techniqueApi.getTechnique(slug);
      setTechnique(response.data);
    } catch (error) {
      console.error('Failed to load technique:', error);
      toast.error('Technique not found');
      navigate('/chat');
    } finally {
      setIsLoadingTechnique(false);
    }
  };

  const loadQuota = async () => {
    try {
      const response = await chatApi.getQuota();
      setQuota(response.data);
    } catch (error) {
      console.error('Failed to load quota:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await techniqueApi.getConversations(slug);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const handleSpeak = (idx, content, action = 'start') => {
    if (speakingIdx === idx && action === 'pause') {
      if (speakingPaused) {
        ttsResume();
        setSpeakingPaused(false);
      } else {
        ttsPause();
        setSpeakingPaused(true);
      }
      return;
    }
    if (speakingIdx === idx && action === 'stop') {
      ttsStop();
      setSpeakingIdx(null);
      setSpeakingPaused(false);
      return;
    }
    if (speakingIdx === idx) {
      ttsStop();
      setSpeakingIdx(null);
      setSpeakingPaused(false);
      return;
    }
    const messageEl = document.querySelector(`[data-msg-idx="${idx}"]`);
    const selectedText = getSelectedTextWithin(messageEl);
    const textToSpeak = selectedText || content;
    const started = ttsSpeak(textToSpeak, {
      onEnd: () => { setSpeakingIdx(null); setSpeakingPaused(false); },
    });
    if (started) {
      setSpeakingIdx(idx);
      setSpeakingPaused(false);
      if (selectedText) {
        toast.success('Reading selected text...', { duration: 2000 });
      }
    }
  };

  // Stop speech / voice session when leaving the page
  useEffect(() => {
    return () => {
      ttsStop();
      if (voiceSessionRef.current) {
        try { voiceSessionRef.current.cancel?.() || voiceSessionRef.current.stop?.(); } catch (e) {}
      }
    };
  }, []);

  const handleMscImage = async (idx, content) => {
    if (mscImageCache.current[idx]) {
      setMscImageData(mscImageCache.current[idx]);
      return;
    }
    try {
      setMscImageLoading(idx);
      toast.loading('Drawing your Mental Summary Chart (20-40 sec)...', { id: 'msc-img', duration: 60000 });
      // Find the student's user message that immediately preceded this AI response —
      // so the extractor can lock the central character from the student's actual words.
      const priorUser = [...messages.slice(0, idx)].reverse().find(m => m.role === 'user');
      const res = await chatApi.generateMscImage(content, priorUser?.content || '');
      const data = res.data;
      mscImageCache.current[idx] = data;
      setMscImageData(data);
      if (data.quota) setQuota(data.quota);
      toast.success('Mental Summary Chart ready!', { id: 'msc-img' });
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to generate Mental Summary Chart';
      toast.error(msg, { id: 'msc-img' });
    } finally {
      setMscImageLoading(null);
    }
  };

  // Downloads a COMPOSITE PNG that includes both the AI-generated image and all
  // the keyword/substitution/story text cards below it. Sharing this on WhatsApp
  // etc. carries the full breakdown along with the picture — the raw AI image
  // alone doesn't contain the text (image models can't render dense text reliably).
  const handleDownloadMscImage = async () => {
    if (!mscImageData?.image) return;
    const el = mscCaptureRef.current;
    if (!el) return;
    const filename = `mental-summary-${(mscImageData.topic || 'chart').replace(/\s+/g, '-').toLowerCase()}.png`;
    try {
      setMscDownloading(true);
      try { await document.fonts.ready; } catch (_) {}
      await new Promise(r => setTimeout(r, 150));
      const { default: html2canvas } = await import('html2canvas');
      const fullW = Math.max(el.scrollWidth, el.getBoundingClientRect().width);
      const fullH = Math.max(el.scrollHeight, el.getBoundingClientRect().height);
      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        allowTaint: true,
        width: fullW,
        height: fullH,
        windowWidth: fullW,
        windowHeight: fullH,
      });
      canvas.toBlob((blob) => {
        if (!blob) { toast.error('Could not create download'); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    } catch (err) {
      console.error('MSC download failed:', err);
      // Fallback: download just the AI image if the composite capture fails
      const a = document.createElement('a');
      a.href = mscImageData.image;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setMscDownloading(false);
    }
  };

  const handleImageMindMap = async (idx, content) => {
    if (imageMindMapCache.current[idx]) {
      setImageMindMapData(imageMindMapCache.current[idx]);
      return;
    }
    try {
      setImageMindMapLoading(idx);
      toast.loading('Generating visual mind map (15-30 sec)...', { id: 'img-mm', duration: 60000 });
      const res = await chatApi.generateMindMapImage(content);
      const data = res.data;
      imageMindMapCache.current[idx] = data;
      setImageMindMapData(data);
      // Visual mind map costs 1 extra question; sync the banner with the server's new quota.
      if (data.quota) setQuota(data.quota);
      toast.success('Mind map ready!', { id: 'img-mm' });
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to generate image mind map';
      toast.error(msg, { id: 'img-mm' });
    } finally {
      setImageMindMapLoading(null);
    }
  };

  const handleDownloadImageMindMap = async () => {
    if (!imageMindMapData?.image) return;
    const filename = `mindmap-${(imageMindMapData.topic || 'visual').replace(/\s+/g, '-').toLowerCase()}.png`;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      const res = await fetch(imageMindMapData.image);
      const blob = await res.blob();

      if (isMobile && navigator.share && navigator.canShare) {
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Visual Mind Map',
              text: imageMindMapData.topic || 'Mind Map'
            });
            return;
          } catch (shareErr) {
            if (shareErr.name === 'AbortError') return;
          }
        }
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Re-ask the question that produced message[idx] using a specific memory technique.
  // Sends via the general chat endpoint (not the current technique's) so the chosen
  // technique drives the retry answer. The retry runs in a FRESH conversation, so we
  // pass the previous assistant answer as explicit source material — otherwise the AI
  // has no access to the uploaded file / original context and invents an off-topic reply.
  // Costs 1 question.
  const handleTryTechnique = async (idx, pickedTechnique) => {
    if (retryLoading !== null || isLoading) return;
    let userQuestion = '';
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user' && messages[i].content) {
        userQuestion = messages[i].content;
        break;
      }
    }
    const sourceAnswer = messages[idx]?.content || '';
    if (!userQuestion && !sourceAnswer) {
      toast.error('Could not find the original content to re-teach.');
      return;
    }
    const prompt = `${pickedTechnique.prompt}

RE-TEACH THE EXACT SAME CONTENT below. Do NOT invent a new topic. Do NOT change the facts, the terms, or the scope — only change the MEMORY TECHNIQUE used to teach them. Keep every single term that was covered; do not skip any.

ORIGINAL STUDENT QUESTION:
${userQuestion || '(the student uploaded a file — see content below)'}

CONTENT TO RE-TEACH (this is the material to memorize — re-cast it with the new technique):
${sourceAnswer}`;
    setRetryLoading(idx);
    setMessages(prev => [...prev, {
      role: 'user',
      content: `🔁 Re-teach with ${pickedTechnique.label}`,
      isRetry: true,
    }]);
    try {
      const response = await chatApi.sendMessage(prompt, null, [], comfortLang, comfortStateCode);
      const { message, quota: newQuota } = response.data;
      setMessages(prev => [...prev, { role: 'assistant', content: message, technique: pickedTechnique.key, userQuery: userQuestion }]);
      if (newQuota) setQuota(newQuota);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to re-teach';
      const errorCode = error.response?.data?.code;
      if (errorCode === 'QUOTA_EXHAUSTED' || errorCode === 'DAILY_LIMIT_REACHED' || errorCode === 'TRIAL_EXPIRED') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: errorMsg,
          isError: true,
          code: errorCode,
        }]);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setRetryLoading(null);
    }
  };

  const handleVoiceToggle = () => {
    if (voiceMode && voiceSessionRef.current) {
      if (voiceMode === 'recording') {
        voiceSessionRef.current.stop();
        setVoiceMode('transcribing');
      } else {
        voiceSessionRef.current.stop?.();
        setVoiceMode(null);
      }
      return;
    }

    if (hasBrowserSTT()) {
      setVoiceMode('listening');
      const session = startBrowserSTT({
        onInterim: (text) => setInput(text),
        onError: (err) => {
          toast.error(err === 'not-allowed' ? 'Mic permission denied' : 'Voice input failed');
          setVoiceMode(null);
          voiceSessionRef.current = null;
        },
        onEnd: ({ finalText }) => {
          if (finalText) setInput(finalText);
          setVoiceMode(null);
          voiceSessionRef.current = null;
        }
      });
      voiceSessionRef.current = session;
    } else if (hasMediaRecorder()) {
      setVoiceMode('recording');
      const session = startWhisperRecording({
        onRecordingStop: () => setVoiceMode('transcribing'),
        onTranscribed: (text) => {
          if (text) setInput((prev) => (prev ? prev + ' ' : '') + text);
          setVoiceMode(null);
          voiceSessionRef.current = null;
        },
        onError: (err) => {
          toast.error(err || 'Voice input failed');
          setVoiceMode(null);
          voiceSessionRef.current = null;
        }
      });
      voiceSessionRef.current = session;
    } else {
      toast.error('Voice input not supported on this browser');
    }
  };

  const handleReviewList = async (idx, content) => {
    if (reviewCache.current[idx]) {
      setReviewListContent(reviewCache.current[idx]);
      return;
    }
    try {
      setReviewLoading(idx);
      const response = await chatApi.generateReviewList(content);
      const reviewList = response.data.reviewList;
      reviewCache.current[idx] = reviewList;
      setReviewListContent(reviewList);
    } catch (error) {
      toast.error('Failed to generate review list');
    } finally {
      setReviewLoading(null);
    }
  };

  const handleDownloadReviewList = async () => {
    if (!reviewContentRef.current) return;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    try {
      toast.loading('Generating image...', { id: 'rl-dl' });
      const html2canvas = (await import('html2canvas')).default;

      // Clone the content into an off-screen 900px-wide stage so the captured PNG
      // has properly laid-out columns even when the modal is narrow on mobile.
      const clone = reviewContentRef.current.cloneNode(true);
      const stage = document.createElement('div');
      stage.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;background:#fff;padding:24px;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#111827;';
      clone.style.cssText = 'width:100%;max-width:none;overflow:visible;';
      stage.appendChild(clone);
      document.body.appendChild(stage);
      stage.querySelectorAll('table').forEach((t) => {
        t.style.width = '100%';
        t.style.tableLayout = 'auto';
        t.style.borderCollapse = 'collapse';
      });
      stage.querySelectorAll('th, td').forEach((c) => {
        c.style.whiteSpace = 'normal';
        c.style.wordBreak = 'normal';
        c.style.overflowWrap = 'anywhere';
        c.style.verticalAlign = 'top';
      });

      let canvas;
      try {
        canvas = await html2canvas(stage, {
          backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true,
          width: 900, windowWidth: 900,
        });
      } finally {
        document.body.removeChild(stage);
      }
      const dataUrl = canvas.toDataURL('image/png');
      if (!dataUrl || dataUrl === 'data:,') { toast.error('Failed to generate image', { id: 'rl-dl' }); return; }

      if (!isMobile) {
        const a = document.createElement('a');
        a.href = dataUrl; a.download = 'review-list.png';
        document.body.appendChild(a); a.click();
        setTimeout(() => document.body.removeChild(a), 3000);
        toast.success('Downloaded!', { id: 'rl-dl' });
      } else {
        toast.dismiss('rl-dl');
        const res = await fetch('/api/download/image', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: dataUrl, filename: 'review-list.png' })
        });
        const { url } = await res.json();
        const serverFileUrl = window.location.origin + url;
        let shared = false;
        try {
          const fileRes = await fetch(serverFileUrl);
          const blob = await fileRes.blob();
          const file = new File([blob], 'review-list.png', { type: blob.type });
          if (navigator.share && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Review List' });
            shared = true;
          }
        } catch (e) {
          if (e.name === 'AbortError') shared = true;
        }
        if (!shared) {
          const a = document.createElement('a');
          a.href = serverFileUrl; a.download = 'review-list.png'; a.target = '_blank';
          document.body.appendChild(a); a.click();
          setTimeout(() => document.body.removeChild(a), 3000);
        }
      }
    } catch (err) {
      console.error('Review list download error:', err);
      toast.error('Failed to download review list', { id: 'rl-dl' });
    }
  };

  const loadConversation = async (convId) => {
    try {
      setIsLoading(true);
      const response = await techniqueApi.getConversation(slug, convId);
      setConversationId(convId);
      setMessages(response.data.messages.map(m => ({
        role: m.role,
        content: m.content
      })));
      setShowHistory(false);
    } catch (error) {
      toast.error('Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this conversation?')) return;

    try {
      await techniqueApi.deleteConversation(slug, convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  // Inline rename of a chat-history row. Commit on Enter/blur, cancel on Escape.
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  const startRenameConversation = (conv, e) => {
    e.stopPropagation();
    setEditingConvId(conv.id);
    setEditingTitle(conv.title || '');
  };

  const cancelRenameConversation = () => {
    setEditingConvId(null);
    setEditingTitle('');
  };

  const commitRenameConversation = async () => {
    const id = editingConvId;
    const title = editingTitle.trim();
    if (!id) return;
    const original = conversations.find(c => c.id === id)?.title || '';
    setEditingConvId(null);
    setEditingTitle('');
    if (!title || title === original) return;
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
    try {
      await techniqueApi.renameConversation(slug, id, title);
      toast.success('Renamed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to rename');
      setConversations(prev => prev.map(c => c.id === id ? { ...c, title: original } : c));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isSmallEnough = file.size <= 10 * 1024 * 1024;
      if (!isValid) toast.error(`${file.name} is not a supported file type`);
      if (!isSmallEnough) toast.error(`${file.name} is too large (max 10MB)`);
      return isValid && isSmallEnough;
    });
    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5));
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!input.trim() && selectedFiles.length === 0) || isLoading) return;

    const userMessage = input.trim();
    const filesToUpload = [...selectedFiles];
    setInput('');
    setIsLoading(true);

    // Create image previews for immediate display
    const imagePreviews = filesToUpload
      .filter(f => f.type.startsWith('image/'))
      .map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    const pdfNames = filesToUpload
      .filter(f => !f.type.startsWith('image/'))
      .map(f => f.name);

    const msgIndex = messages.length;
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage || '',
      images: imagePreviews,
      pdfNames
    }]);

    try {
      // Upload files first if any
      let fileIds = [];
      if (filesToUpload.length > 0) {
        setIsUploading(true);
        const uploadResponse = await uploadApi.uploadFiles(filesToUpload);
        fileIds = uploadResponse.data.files.map(f => f.id);

        // Replace blob previews with server URLs
        const serverImages = uploadResponse.data.files
          .filter(f => f.fileType === 'image')
          .map(f => ({ name: f.originalName, url: f.fileUrl }));
        if (serverImages.length > 0) {
          setMessages(prev => prev.map((m, i) =>
            i === msgIndex ? { ...m, images: serverImages } : m
          ));
        }

        setSelectedFiles([]);
        setIsUploading(false);
      }

      // Auto-detect comfort language from the typed message when still at default.
      let effectiveLang = comfortLang;
      if (effectiveLang === 'en' && userMessage) {
        const detected = detectComfortLanguageFromText(userMessage);
        if (detected) {
          effectiveLang = detected;
          changeComfortLang(detected);
          toast.success(`Switched to ${COMFORT_LANGUAGES.find(l => l.code === detected)?.label}. Change anytime from the 🌐 chip.`, { duration: 4000 });
        }
      }
      const response = await techniqueApi.sendMessage(slug, userMessage || 'Please analyze the attached file(s).', conversationId, fileIds, effectiveLang, comfortStateCode);
      const { message, conversationId: convId, quota: newQuota } = response.data;

      setConversationId(convId);
      setMessages(prev => [...prev, { role: 'assistant', content: message }]);
      setQuota(newQuota);

      // Refresh conversations list
      loadConversations();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to send message';
      const errorCode = error.response?.data?.code;

      if (errorCode === 'QUOTA_EXHAUSTED' || errorCode === 'DAILY_LIMIT_REACHED' || errorCode === 'TRIAL_EXPIRED') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: errorMsg,
          isError: true,
          code: errorCode
        }]);
      } else {
        toast.error(errorMsg);
        setMessages(prev => prev.slice(0, -1));
      }
      setSelectedFiles([]);
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setSelectedFiles([]);
    setShowHistory(false);
  };

  const renderQuotaInfo = () => {
    if (!quota) return null;

    if (quota.tier === 'L1') {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 lg:py-2 mt-1 lg:mt-2">
          <div className="flex items-center justify-between text-xs lg:text-sm">
            <div>
              <span className="text-amber-800 font-medium">Trial Mode</span>
              <span className="text-amber-600 ml-2">
                {quota.totalLimit ? `${quota.questionsRemaining} left` : `${quota.questionsRemainingToday} today`}
              </span>
            </div>
            <span className="text-amber-600">
              {quota.daysRemaining}d remaining
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30 rounded-lg px-3 py-1.5 lg:py-2 mt-1 lg:mt-2">
        <div className="flex items-center justify-between text-xs lg:text-sm">
          <span className="text-primary-800 dark:text-primary-200">
            <span className="font-medium">{quota.availableQuestions}</span> questions available
          </span>
          {quota.availableQuestions < 20 && (
            <Link to="/recharge" className="text-primary-600 dark:text-primary-400 hover:underline">
              Recharge now
            </Link>
          )}
        </div>
      </div>
    );
  };

  const getSuggestions = () => {
    if (!technique) return [];

    const suggestionsBySlug = {
      'memory-palace': [
        'How do I create my first Memory Palace?',
        'What locations work best for a Memory Palace?',
        'Help me memorize a grocery list using Memory Palace'
      ],
      'peg-system': [
        'What is the Peg System?',
        'Teach me the number-rhyme peg system',
        'How do I use pegs to memorize a list?'
      ],
      'major-system': [
        'Explain the Major System consonant codes',
        'Convert the number 47 using Major System',
        'How do I practice the Major System?'
      ],
      'link-method': [
        'How does the Link Method work?',
        'Help me chain together 10 random items',
        'What makes a good linking story?'
      ],
      'spaced-repetition': [
        'What is the ideal review schedule?',
        'How does the forgetting curve work?',
        'Help me create a study plan'
      ]
    };

    return suggestionsBySlug[slug] || [
      `What is ${technique.name}?`,
      `How do I get started with ${technique.name}?`,
      `Give me a practice exercise`
    ];
  };

  const userTier = quota?.tier || user?.tier;
  const hasLevelAccess = !technique?.levels || technique.levels.includes(userTier);

  if (isLoadingTechnique || !quota) {
    return (
      <div className="h-dvh flex flex-col bg-gray-50 dark:bg-gray-950">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center">
          <button
            onClick={() => window.dispatchEvent(new Event('open-sidebar'))}
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg lg:hidden mr-1"
            title="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/chat" className="mr-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <span className="text-lg font-semibold dark:text-gray-100">Loading...</span>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Level-gated view
  if (technique && !hasLevelAccess) {
    const requiredLevel = technique.levels?.find(l => l !== user?.tier) || 'L2';
    const isL1User = user?.tier === 'L1';
    const trainerName = user?.trainerName;
    return (
      <div className="h-dvh flex flex-col">
        <div className="p-4 border-b bg-white">
          <div className="flex items-center">
            <button
              onClick={() => window.dispatchEvent(new Event('open-sidebar'))}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg lg:hidden mr-1"
              title="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/chat" className="mr-3 text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-semibold">{technique.name}</h1>
              <p className="text-sm text-gray-500">{technique.description}</p>
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{technique.name}</h2>
            <p className="text-gray-500 mb-6">{technique.description}</p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              {isL1User ? (
                <>
                  <p className="text-amber-800 font-medium">
                    This technique requires {requiredLevel} access
                  </p>
                  <p className="text-amber-700 text-sm mt-2">
                    {trainerName
                      ? `Please contact your trainer ${trainerName} to upgrade your account.`
                      : 'Please contact your administrator to upgrade your account.'
                    }
                  </p>
                </>
              ) : (
                <>
                  <p className="text-amber-800 font-medium">
                    Upgrade to {requiredLevel} Level to access this technique
                  </p>
                  <Link
                    to="/recharge"
                    className="inline-block mt-3 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                  >
                    Recharge Now
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="px-3 py-2 lg:p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between mb-1 lg:mb-2">
          <div className="flex items-center min-w-0 flex-1">
            <button
              onClick={() => window.dispatchEvent(new Event('open-sidebar'))}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg lg:hidden mr-1 shrink-0"
              title="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/chat" className="mr-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="text-base lg:text-xl font-semibold truncate dark:text-gray-100">{technique?.name}</h1>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 truncate hidden lg:block">{technique?.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-1 lg:space-x-2 shrink-0">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors ${
                showHistory ? 'bg-primary-100 text-primary-600' : 'text-gray-500 hover:bg-gray-100'
              }`}
              title="Chat History"
            >
              <History className="w-5 h-5" />
              <span className="text-sm hidden lg:inline">History</span>
            </button>
            <button
              onClick={startNewChat}
              className="text-xs lg:text-sm text-primary-600 hover:text-primary-700 px-2 lg:px-3 py-1 whitespace-nowrap"
            >
              New Chat
            </button>
          </div>
        </div>
        {renderQuotaInfo()}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* History Sidebar */}
        {showHistory && (
          <div className="w-64 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <div className="p-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Previous Chats</h3>
              {conversations.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No previous conversations</p>
              ) : (
                <div className="space-y-1">
                  {conversations.map(conv => {
                    const isEditing = editingConvId === conv.id;
                    return (
                    <div
                      key={conv.id}
                      onClick={() => !isEditing && loadConversation(conv.id)}
                      className={`p-2 rounded-lg group flex items-center justify-between ${isEditing ? '' : 'cursor-pointer'} ${
                        conversationId === conv.id
                          ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-200'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <input
                            autoFocus
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onBlur={commitRenameConversation}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); commitRenameConversation(); }
                              else if (e.key === 'Escape') { e.preventDefault(); cancelRenameConversation(); }
                            }}
                            maxLength={200}
                            className="w-full text-sm px-1.5 py-0.5 bg-white dark:bg-gray-900 border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        ) : (
                          <p className="text-sm truncate">{conv.title || 'Untitled'}</p>
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {new Date(conv.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {!isEditing && (
                        <div className="flex items-center">
                          <button
                            onClick={(e) => startRenameConversation(conv, e)}
                            className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-opacity"
                            title="Rename"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500 hover:text-red-500 transition-opacity"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Learn {technique?.name}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-6">
                  {technique?.description}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {getSuggestions().map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>

                {/* Trainer-attached videos for this technique. Hidden if none. */}
                {techniqueLessons.length > 0 && (
                  <div className="mt-10 max-w-3xl mx-auto text-left">
                    <div className="flex items-center gap-2 mb-3">
                      <Video className="w-4 h-4 text-primary-600" />
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Related Videos</h3>
                      <span className="text-xs text-gray-400">({techniqueLessons.length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {techniqueLessons.map(lesson => (
                        <button
                          key={lesson.id}
                          onClick={() => navigate(`/lms/lessons/${lesson.id}`)}
                          className="group flex items-start gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 hover:shadow-sm transition-all text-left"
                        >
                          {lesson.youtube_video_id ? (
                            <div className="relative w-24 h-16 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                              <img
                                src={`https://img.youtube.com/vi/${lesson.youtube_video_id}/mqdefault.jpg`}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Play className="w-6 h-6 text-white drop-shadow" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-24 h-16 flex-shrink-0 rounded bg-primary-50 flex items-center justify-center">
                              <Video className="w-6 h-6 text-primary-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{lesson.title}</div>
                            {lesson.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{lesson.description}</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex message-fade-in ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-primary-600 text-white'
                      : msg.isError
                        ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100'
                        : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100'
                  }`}
                >
                  {msg.isError && (
                    <div className="flex items-center text-red-600 mb-2">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      <span className="font-medium">Limit Reached</span>
                    </div>
                  )}
                  {msg.role === 'user' || msg.isError ? (
                    <div>
                      {msg.images && msg.images.length > 0 && (
                        <div className={`flex flex-wrap gap-2 ${msg.content ? 'mb-2' : ''}`}>
                          {msg.images.map((img, i) => (
                            <AttachmentImage key={i} src={img.url} alt={img.name} />
                          ))}
                        </div>
                      )}
                      {msg.pdfNames && msg.pdfNames.length > 0 && (
                        <div className={`flex flex-wrap gap-1 ${msg.content ? 'mb-2' : ''}`}>
                          {msg.pdfNames.map((name, i) => (
                            <span key={i} className="inline-flex items-center text-xs bg-white/20 rounded px-2 py-1">
                              <FileText className="w-3 h-3 mr-1" /> {name}
                            </span>
                          ))}
                        </div>
                      )}
                      {msg.content && (
                        <p className={`whitespace-pre-wrap ${msg.isError ? 'text-red-700' : ''}`}>{msg.content}</p>
                      )}
                    </div>
                  ) : (
                    <div data-msg-idx={idx}>
                      <div className="prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0
                        [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_table]:border-gray-200 [&_table]:my-2
                        [&_th]:bg-gray-50 [&_th]:font-semibold [&_th]:px-2 [&_th]:py-1 [&_th]:border [&_th]:border-gray-200 [&_th]:text-left [&_th]:text-xs
                        [&_td]:px-2 [&_td]:py-1 [&_td]:border [&_td]:border-gray-200 [&_td]:text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>
                      <div className="mt-2 flex items-center space-x-3">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(msg.content).catch(() => {});
                            setCopiedIdx(idx);
                            setTimeout(() => setCopiedIdx(null), 2000);
                          }}
                          className="flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy response"
                        >
                          {copiedIdx === idx ? <><Check className="w-3 h-3 mr-1" /> Copied!</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                        </button>
                        <button
                          onClick={() => setMindMapIdx(idx)}
                          className="flex items-center text-xs text-gray-400 hover:text-indigo-600 transition-colors"
                          title="View as Mind Map"
                        >
                          <GitBranch className="w-3 h-3 mr-1" /> Mind Map
                        </button>
                        <button
                          onClick={() => handleReviewList(idx, msg.content)}
                          disabled={reviewLoading === idx}
                          className="flex items-center text-xs text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                          title="View Review List"
                        >
                          {reviewLoading === idx ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                          ) : (
                            <><ClipboardList className="w-3 h-3 mr-1" /> Review</>
                          )}
                        </button>
                        {(slug === 'mental-summary-charts' || msg.technique === 'mental-summary-charts') && (
                          <button
                            onClick={() => handleMscImage(idx, msg.content)}
                            disabled={mscImageLoading === idx}
                            className="flex items-center text-xs text-gray-400 hover:text-orange-600 transition-colors disabled:opacity-50"
                            title="Draw as Mental Summary Chart (1 question)"
                          >
                            {mscImageLoading === idx ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Drawing...</>
                            ) : (
                              <><Pencil className="w-3 h-3 mr-1" /> Mental Summary</>
                            )}
                          </button>
                        )}
                        {isSpeechSupported() && (
                          speakingIdx === idx ? (
                            <>
                              <button
                                onClick={() => handleSpeak(idx, msg.content, 'pause')}
                                className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                                title={speakingPaused ? 'Resume' : 'Pause'}
                              >
                                {speakingPaused ? (
                                  <><Play className="w-3 h-3 mr-1" /> Resume</>
                                ) : (
                                  <><Pause className="w-3 h-3 mr-1" /> Pause</>
                                )}
                              </button>
                              <button
                                onClick={() => handleSpeak(idx, msg.content, 'stop')}
                                className="flex items-center text-xs text-gray-400 hover:text-red-600 transition-colors"
                                title="Stop"
                              >
                                <VolumeX className="w-3 h-3 mr-1" /> Stop
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleSpeak(idx, msg.content)}
                              className="flex items-center text-xs text-gray-400 hover:text-blue-600 transition-colors"
                              title="Listen (select text first to read only that portion)"
                            >
                              <Volume2 className="w-3 h-3 mr-1" /> Listen
                            </button>
                          )
                        )}
                        <button
                          onClick={async () => {
                            if (chargedVisualRef.current.has(idx)) {
                              setInfographicIdx(idx);
                              return;
                            }
                            try {
                              const res = await chatApi.chargeVisualMindMap();
                              if (res.data?.quota) setQuota(res.data.quota);
                              chargedVisualRef.current.add(idx);
                              setInfographicIdx(idx);
                            } catch (err) {
                              const msg = err.response?.data?.error || 'Could not open Visual Mind Map';
                              toast.error(msg);
                            }
                          }}
                          className="flex items-center text-xs text-gray-400 hover:text-pink-600 transition-colors"
                          title="View as Visual Mind Map (costs 2 questions)"
                        >
                          <Palette className="w-3 h-3 mr-1" /> Visual
                        </button>
                      </div>
                      <TechniqueChips
                        onPick={(t) => handleTryTechnique(idx, t)}
                        disabled={retryLoading !== null}
                        activeIdx={retryLoading}
                        idx={idx}
                        excludeKey={slug}
                      />
                    </div>
                  )}
                  {msg.code === 'QUOTA_EXHAUSTED' && (
                    <Link
                      to="/recharge"
                      className="mt-2 inline-block text-primary-600 hover:underline text-sm"
                    >
                      Buy more questions
                    </Link>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Selected Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((file, idx) => (
                  <div
                    key={idx}
                    className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:text-gray-200"
                  >
                    {file.type.startsWith('image/') ? (
                      <Image className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
                    )}
                    <span className="truncate max-w-[150px]">{file.name}</span>
                    <button
                      onClick={() => removeFile(idx)}
                      className="ml-2 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                <span className="hidden sm:inline">Pick </span>reply language &amp; your state:
              </span>
              <ComfortLanguageChip value={comfortLang} onChange={changeComfortLang} />
              <StateChip value={comfortStateCode} onChange={changeComfortState} />
            </div>
            <div className="flex items-end space-x-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,.pdf"
                multiple
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-3 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/15 rounded-xl transition-colors shrink-0"
                title="Attach files (images or PDFs)"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if ((input.trim() || selectedFiles.length > 0) && !isLoading && !isUploading) handleSubmit(e);
                  }
                }}
                placeholder={selectedFiles.length > 0
                  ? "Add a message about the files..."
                  : `Ask about ${technique?.name}...`
                }
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none overflow-y-auto"
                style={{ minHeight: '48px', maxHeight: '150px' }}
                rows={1}
                disabled={isLoading || isUploading}
              />
              {isVoiceInputSupported() && (
                <button
                  type="button"
                  onClick={handleVoiceToggle}
                  disabled={isLoading || isUploading || voiceMode === 'transcribing'}
                  className={`p-3 rounded-xl transition-colors shrink-0 ${
                    voiceMode === 'listening' || voiceMode === 'recording'
                      ? 'bg-red-100 text-red-600 animate-pulse'
                      : voiceMode === 'transcribing'
                        ? 'bg-gray-100 text-gray-500'
                        : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                  title={
                    voiceMode === 'listening' ? 'Stop listening'
                    : voiceMode === 'recording' ? 'Stop recording'
                    : voiceMode === 'transcribing' ? 'Transcribing...'
                    : 'Voice input'
                  }
                >
                  {voiceMode === 'transcribing' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : voiceMode === 'listening' || voiceMode === 'recording' ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading || isUploading || (!input.trim() && selectedFiles.length === 0)}
                className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {isLoading || isUploading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Mind Map Modal */}
      {mindMapIdx !== null && messages[mindMapIdx] && (
        <Suspense fallback={null}>
          <MindMapModal
            markdown={messages[mindMapIdx].content}
            title={(() => {
              for (let i = mindMapIdx - 1; i >= 0; i--) {
                if (messages[i]?.role === 'user' && messages[i].content) return messages[i].content;
              }
              return technique?.name || '';
            })()}
            onClose={() => setMindMapIdx(null)}
          />
        </Suspense>
      )}
      {/* Infographic Notes Mind Map Modal */}
      {infographicIdx !== null && messages[infographicIdx] && (
        <Suspense fallback={null}>
          <InfographicMindMap
            markdown={messages[infographicIdx].content}
            onClose={() => setInfographicIdx(null)}
          />
        </Suspense>
      )}
      {/* Mental Summary Chart Modal — ONE poster layout used for both on-screen
          preview and download. Image sits in the middle, numbered cards with
          keywords / substitutions / story wrap around it on both sides — so what
          the student sees is exactly what the downloaded PNG shows. */}
      {mscImageData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => setMscImageData(null)}>
          <div className="bg-white rounded-2xl max-w-[95vw] w-full max-h-[95vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Pencil className="w-5 h-5 mr-2 text-orange-600" /> Mental Summary Chart
                </h3>
                {mscImageData.topic && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {mscImageData.topic}
                    {mscImageData.character ? ` · central image: ${mscImageData.character}` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownloadMscImage}
                  disabled={mscDownloading}
                  className="flex items-center px-3 py-1.5 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-60"
                >
                  {mscDownloading ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Preparing...</>
                  ) : (
                    <><Download className="w-4 h-4 mr-1" /> Download poster</>
                  )}
                </button>
                <button onClick={() => setMscImageData(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* The poster — this exact DOM is what gets captured for download. */}
            <div
              ref={mscCaptureRef}
              className="p-4 sm:p-6"
              style={{ background: '#fffbea' }}
            >
              {/* Title banner */}
              <div className="text-center mb-5">
                <div
                  className="inline-block px-6 sm:px-10 py-2"
                  style={{ border: '3px solid #8b4513', background: '#fff8dc', color: '#4a2818', borderRadius: '10px' }}
                >
                  <div className="text-xl sm:text-2xl font-black tracking-wider">{(mscImageData.topic || 'Mental Summary Chart').toUpperCase()}</div>
                  <div className="text-[10px] sm:text-xs mt-1 tracking-widest" style={{ color: '#8b4513' }}>MENTAL SUMMARY CHART</div>
                </div>
              </div>

              {(() => {
                const arr = Array.isArray(mscImageData.labels) ? mscImageData.labels : [];
                const mid = Math.ceil(arr.length / 2);
                const leftLabels = arr.slice(0, mid);
                const rightLabels = arr.slice(mid);

                const renderCard = (l, i) => (
                  <div
                    key={i}
                    className="mb-2.5"
                    style={{ background: '#fff', border: '2px solid #d4a054', borderRadius: '10px', padding: '10px 12px', boxShadow: '2px 2px 0 #b8863a' }}
                  >
                    <div className="flex items-start">
                      <span
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#c0392b', color: '#fff', fontWeight: 900, fontSize: '14px', marginRight: '8px' }}
                      >{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>{l.text}</div>
                        <div style={{ fontSize: '10px', color: '#8b4513', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '2px' }}>
                          Body: {l.body_part}
                        </div>
                      </div>
                    </div>
                    {Array.isArray(l.keywords) && l.keywords.length > 0 && (
                      <div className="mt-1.5">
                        <div style={{ fontSize: '9px', fontWeight: 700, color: '#666', letterSpacing: '1px' }}>KEYWORDS</div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {l.keywords.map((k, j) => (
                            <span key={j} style={{ fontSize: '11px', background: '#fff8dc', border: '1px solid #d4a054', color: '#4a2818', padding: '2px 8px', borderRadius: '999px' }}>{k}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {l.substitutions && (
                      <div className="mt-1.5">
                        <div style={{ fontSize: '9px', fontWeight: 700, color: '#666', letterSpacing: '1px' }}>SUBSTITUTIONS (PEG)</div>
                        <div style={{ fontSize: '12px', fontFamily: 'ui-monospace, "Courier New", monospace', color: '#1a1a1a', background: '#fff8dc', padding: '3px 6px', borderRadius: '4px', marginTop: '2px' }}>{l.substitutions}</div>
                      </div>
                    )}
                    {l.story && (
                      <div className="mt-1.5">
                        <div style={{ fontSize: '9px', fontWeight: 700, color: '#666', letterSpacing: '1px' }}>STORY</div>
                        <div style={{ fontSize: '12px', color: '#333', fontStyle: 'italic', lineHeight: 1.35, marginTop: '2px' }}>{l.story}</div>
                      </div>
                    )}
                  </div>
                );

                return (
                  <div
                    className="grid gap-3 sm:gap-4"
                    style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1fr)' }}
                  >
                    <div>{leftLabels.map((l, i) => renderCard(l, i))}</div>
                    <div className="text-center">
                      <img
                        src={mscImageData.image}
                        alt=""
                        crossOrigin="anonymous"
                        style={{ maxWidth: '100%', height: 'auto', borderRadius: '12px', border: '3px solid #8b4513', boxShadow: '4px 4px 0 #b8863a' }}
                      />
                      {mscImageData.character && (
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#4a2818', fontStyle: 'italic' }}>
                          Central image: <strong>{mscImageData.character}</strong>
                        </div>
                      )}
                    </div>
                    <div>{rightLabels.map((l, i) => renderCard(l, mid + i))}</div>
                  </div>
                );
              })()}

              <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#8b4513', letterSpacing: '2px' }}>
                SEE ONCE · CONNECT DEEPLY · REMEMBER FOREVER
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Image Mind Map Modal */}
      {imageMindMapData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setImageMindMapData(null)}>
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-pink-600" /> Visual Mind Map
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownloadImageMindMap}
                  className="flex items-center px-3 py-1.5 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" /> Download
                </button>
                <button onClick={() => setImageMindMapData(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <img src={imageMindMapData.image} alt="Visual Mind Map" className="w-full h-auto rounded-lg border border-gray-200" />
              {imageMindMapData.topic && (
                <p className="text-center text-sm text-gray-500 mt-3">Topic: <span className="font-medium text-gray-700">{imageMindMapData.topic}</span></p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Review List Modal */}
      {reviewListContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setReviewListContent(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ClipboardList className="w-5 h-5 mr-2 text-emerald-600" /> Review List
              </h3>
              <div className="flex items-center space-x-1">
                <button onClick={handleDownloadReviewList} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" title="Download as PNG">
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => setReviewListContent(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div ref={reviewContentRef} className="p-4 prose prose-sm max-w-none prose-table:my-0 overflow-x-auto
              [&_table]:w-full [&_table]:border-collapse [&_table]:rounded-xl [&_table]:overflow-hidden [&_table]:shadow-sm
              [&_thead]:bg-gradient-to-r [&_thead]:from-purple-200 [&_thead]:via-pink-200 [&_thead]:to-orange-200
              [&_th]:text-purple-900 [&_th]:font-bold [&_th]:px-4 [&_th]:py-3 [&_th]:border-b-2 [&_th]:border-purple-300 [&_th]:text-left [&_th]:text-sm [&_th]:uppercase [&_th]:tracking-wide
              [&_td]:px-4 [&_td]:py-2.5 [&_td]:border-b [&_td]:border-purple-100 [&_td]:text-sm [&_td]:text-gray-800
              [&_tr:nth-child(odd)]:bg-sky-50
              [&_tr:nth-child(even)]:bg-pink-50
              [&_tbody_tr:hover]:bg-yellow-100 [&_tbody_tr]:transition-colors">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{
                (() => {
                  let text = reviewListContent.replace(/^.*?(review\s*list).*?\n/i, '').trim();
                  const lines = text.split('\n');
                  const fixed = [];
                  let headerColCount = 0;
                  let hasSeparator = false;

                  for (let i = 0; i < lines.length; i++) {
                    let line = lines[i].trim();
                    if (!line) continue;
                    if (!line.includes('|')) continue;
                    if (!line.startsWith('|')) line = '| ' + line;
                    if (!line.endsWith('|')) line = line + ' |';

                    const isSeparator = line.replace(/[|\s:-]/g, '').length === 0 && line.includes('-');
                    if (isSeparator) {
                      const cols = headerColCount || 3;
                      fixed.push('| ' + Array(cols).fill('---').join(' | ') + ' |');
                      hasSeparator = true;
                    } else {
                      const colCount = line.split('|').filter(c => c.trim() !== '').length;
                      if (!headerColCount && colCount > 0) headerColCount = colCount;
                      fixed.push(line);
                      if (fixed.length === 1 && !hasSeparator) {
                        const cols = colCount || 3;
                        fixed.push('| ' + Array(cols).fill('---').join(' | ') + ' |');
                        hasSeparator = true;
                      }
                    }
                  }

                  if (fixed.length >= 1 && !hasSeparator) {
                    const cols = headerColCount || 3;
                    fixed.splice(1, 0, '| ' + Array(cols).fill('---').join(' | ') + ' |');
                  }

                  return fixed.join('\n') || reviewListContent;
                })()
              }</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechniqueChat;
